// SCR-STAFF-617: بارگذاری موبایل با QR (US-DOC-154) — بدون داده پرونده.
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { unwrap } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { PageHeader } from '@phoenix/ui';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router';
import { SCR_STAFF_617 } from './common';

/**
 * بارگذاری فایل به جلسه handoff با توکن مسیر.
 * مسیر قراردادی عمومی: POST /api/v1/documents/public/upload-handoffs/{token}/files (multipart file).
 * اگر بک‌اند هنوز این Endpoint را نداشته باشد، خطا نمایش داده می‌شود (گزارش gap).
 */
export function MobileUploadPage() {
  const { t } = useTranslation('doc');
  const { tenantApi } = useTenantSession();
  const { token } = useParams();
  const [uploaded, setUploaded] = useState(0);

  const upload = useMutation({
    mutationFn: async (files: FileList) => {
      if (!token) {
        throw new Error('missing-token');
      }
      let count = 0;
      for (const file of Array.from(files)) {
        const form = new FormData();
        form.append('file', file);
        await unwrap(
          tenantApi.POST('/api/v1/documents/public/upload-handoffs/{token}/files', {
            params: { path: { token } },
            body: form as never,
            bodySerializer: (body: unknown) => body as FormData,
          }),
        );
        count += 1;
        setUploaded(count);
      }
      return count;
    },
  });

  return (
    <Stack spacing={2} data-testid="mobile-upload-page" data-screen-id={SCR_STAFF_617} sx={{ p: 2, maxWidth: 480, mx: 'auto' }}>
      <PageHeader titleKey="doc:uploadHandoff.title" />
      <Alert severity="warning">{t('uploadHandoff.noRecordData')}</Alert>
      {!token ? (
        <Alert severity="error">{t('uploadHandoff.missingToken')}</Alert>
      ) : (
        <>
          <Button variant="contained" component="label" disabled={upload.isPending} data-testid="mobile-upload-pick">
            {t('uploadHandoff.pickFiles')}
            <input
              hidden
              type="file"
              multiple
              accept="image/*,application/pdf"
              onChange={(event) => {
                const files = event.target.files;
                if (files?.length) {
                  upload.mutate(files);
                }
                event.target.value = '';
              }}
            />
          </Button>
          {upload.isPending ? (
            <>
              <Typography variant="body2">{t('uploadHandoff.uploading')}</Typography>
              <LinearProgress />
            </>
          ) : null}
          {upload.isSuccess ? (
            <Alert severity="success" data-testid="mobile-upload-done">
              {t('uploadHandoff.uploaded', { count: uploaded || upload.data })}
            </Alert>
          ) : null}
          {upload.isError ? (
            <Alert severity="error" data-testid="mobile-upload-error">
              {t('uploadHandoff.error')}
            </Alert>
          ) : null}
        </>
      )}
    </Stack>
  );
}
